import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step as MuiStep,
  StepLabel,
  Button,
  Badge,
  Alert,
  Snackbar,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CondicaoPagto, FinalizarPedidoDto, Pedido } from '@azoupvendas/shared';
import api from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';
import { db } from '@/db/dexie';
import { opLogService } from '@/db/op-log.service';
import { usePedidoStore } from '../../stores/pedido.store';
import { useParcelasCalc } from '../../hooks/useParcelasCalc';
import { AutosaveQueue, marcarNaoRetryavel } from '../../services/autosave-queue';
import { pedidoClientService } from '../../services/pedido.service';
import { Step1ClienteSelect } from './Step1ClienteSelect';
import { Step2Catalogo } from './Step2Catalogo';
import { Step3CarrinhoManaged } from './Step3CarrinhoManaged';
import { Step4PagamentoManaged } from './Step4PagamentoManaged';
import { Step5Confirmacao } from './Step5Confirmacao';
import { ShareModal } from '../ShareModal';

// AIDEV-NOTE: O wizard gerencia a navegacao entre steps via pedidoStore.stepAtual.
// Estado compartilhado entre steps (desconto geral, observacao, condicao) e mantido
// no wizard para evitar perda ao montar/desmontar steps.
// AIDEV-NOTE: Auto-save progressivo — rascunho criado ao sair do Step 0 (cliente),
// itens salvos via PATCH /pedidos/:id/itens com debounce de 1500ms.

interface OrigemSyncPedido {
  codigo: number;
  descricao: string;
}

interface TipoMovimentoReceber {
  codigo: number;
  descricao: string;
}

const STEPS = ['Cliente', 'Produtos', 'Carrinho', 'Pagamento', 'Confirmar'];

// AIDEV-NOTE: editMode=true pula criacao de rascunho (pedidoId ja existe no store)
// onCancel navega para fora do wizard sem salvar (usado no modo edicao)
// initialValues restaura estado de pagamento ao editar pedido ja finalizado
interface PedidoWizardProps {
  editMode?: boolean;
  onCancel?: () => void;
  // AIDEV-NOTE: status atual do pedido em edicao — usado para decidir se o autosave
  // de itens deve disparar (so faz sentido enquanto o pedido ainda e 'rascunho' no server)
  pedidoStatus?: string;
  // AIDEV-NOTE: revision otimista do rascunho (Task 7) — EditarPedidoPage popula com
  // pedido.revision para o wizard iniciar o revisionRef sincronizado com o server.
  // Em modo criacao o rascunho sempre nasce com revision 0 (default da coluna).
  initialRevision?: number;
  initialValues?: {
    condicaoId?: number | null;
    tipoMovimentoReceberId?: number | null;
    observacao?: string;
    origem?: string;
    descontoGeral?: number;
  };
}

type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function PedidoWizard({
  editMode,
  onCancel,
  pedidoStatus,
  initialRevision,
  initialValues
}: PedidoWizardProps = {}) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const stepAtual = usePedidoStore((s) => s.stepAtual);
  const setStepAtual = usePedidoStore((s) => s.setStepAtual);
  const itens = usePedidoStore((s) => s.itens);
  const clienteSelecionado = usePedidoStore((s) => s.clienteSelecionado);
  const pedidoId = usePedidoStore((s) => s.pedidoId);
  const setPedidoId = usePedidoStore((s) => s.setPedidoId);
  const tabelaPrecoId = usePedidoStore((s) => s.tabelaPrecoId);
  const setTabelaPreco = usePedidoStore((s) => s.setTabelaPreco);
  const resetPedido = usePedidoStore((s) => s.resetPedido);
  const setDraftOrigem = usePedidoStore((s) => s.setDraftOrigem);
  const setDraftVendedorId = usePedidoStore((s) => s.setDraftVendedorId);

  const [tabToast, setTabToast] = useState<string | null>(null);
  const [pedidoFinalizado, setPedidoFinalizado] = useState<Pedido | null>(null);
  // AIDEV-NOTE: Guarda de regressao (Task 8) — armazena a mensagem do server (com os dois
  // totais) quando o finalizar detecta queda suspeita >50% vs o rascunho salvo. Dialog pede
  // confirmacao explicita antes de reenviar com confirmar_reducao: true.
  const [confirmReducao, setConfirmReducao] = useState<string | null>(null);

  // AIDEV-NOTE: Busca políticas frescas do banco ao abrir o wizard — garante que
  // restricoes_condicao_pagto_jwt e tipo_movimento_receber_padrao_id reflitam o
  // que o admin configurou sem exigir reload da página ou novo login.
  useEffect(() => {
    refreshUser().catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [shareOpen, setShareOpen] = useState(false);
  // AIDEV-NOTE: true quando o finalizar caiu no fallback offline (op_log) — a UI precisa dizer
  // isso ao vendedor, senao ele acha que o pedido ja chegou ao ERP e nao confere depois.
  const [finalizadoOffline, setFinalizadoOffline] = useState(false);
  // AIDEV-NOTE: autoSaveStatus exibe indicador visual de sincronizacao no rodape do wizard
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');
  // AIDEV-NOTE: ref para o timer do debounce — cancelado ao desmontar ou ao mudar itens antes de disparar
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // AIDEV-NOTE: Versionamento otimista do rascunho (Task 7) — revisionRef acompanha a revision
  // que o wizard conhece. Em criacao o rascunho sempre nasce com revision 0; em edicao,
  // EditarPedidoPage passa pedido.revision via prop. undefined mantem o comportamento legado
  // (server aplica sem checagem) caso o valor nao esteja disponivel.
  const revisionRef = useRef<number | undefined>(editMode ? initialRevision : 0);

  // AIDEV-NOTE: Estado local do wizard compartilhado entre Step3, Step4 e Step5
  // initialValues restaura os dados de pagamento ao editar um pedido ja finalizado
  const [descontoGeral, setDescontoGeral] = useState(initialValues?.descontoGeral ?? 0);
  const [origem, setOrigem] = useState<string>(initialValues?.origem ?? '');
  const [observacao, setObservacao] = useState(initialValues?.observacao ?? '');
  const [condicaoId, setCondicaoId] = useState<number | null>(initialValues?.condicaoId ?? null);
  // AIDEV-NOTE: tipo_movimento_receber selecionado no Step4 — prioriza initialValues, depois padrão do JWT
  const [tipoMovimentoReceberId, setTipoMovimentoReceberId] = useState<number | null>(
    initialValues?.tipoMovimentoReceberId !== undefined
      ? initialValues.tipoMovimentoReceberId
      : (user?.tipo_movimento_receber_padrao_id ?? null)
  );

  // AIDEV-NOTE: Busca condicoes de pagamento no nivel do wizard para compartilhar entre Step3/Step4/Step5
  const { data: condicoes = [] } = useQuery({
    queryKey: ['condicoes-pagto'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<CondicaoPagto[]> => {
      const res = await api.get<{ condicoes: CondicaoPagto[] }>('/pedidos/condicoes-pagto');
      return res.data.condicoes;
    }
  });

  // AIDEV-NOTE: Busca origens de pedido da API (sync_origens_pedido)
  const { data: origens = [] } = useQuery({
    queryKey: ['origens-pedido'],
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<OrigemSyncPedido[]> => {
      const res = await api.get<{ origens: OrigemSyncPedido[] }>('/pedidos/origens-pedido');
      return res.data.origens;
    }
  });

  // AIDEV-NOTE: Busca tipos de movimento receber da API (sync_tipos_movimento_receber)
  const { data: tiposMovimentoReceber = [] } = useQuery({
    queryKey: ['tipos-movimento-receber'],
    staleTime: 10 * 60_000,
    queryFn: async (): Promise<TipoMovimentoReceber[]> => {
      const res = await api.get<{ tipos: TipoMovimentoReceber[] }>(
        '/pedidos/tipos-movimento-receber'
      );
      return res.data.tipos;
    }
  });

  const condicaoSelecionada = useMemo(
    () => condicoes.find((c) => c.id === condicaoId) ?? null,
    [condicoes, condicaoId]
  );

  // AIDEV-NOTE: Calcula subtotal e total para parcelas
  const subtotal = useMemo(
    () =>
      itens.reduce(
        (acc, item) =>
          acc + item.valor_unitario * item.quantidade * (1 - (item.desconto_percentual ?? 0) / 100),
        0
      ),
    [itens]
  );
  const totalProdutos = subtotal * (1 - descontoGeral / 100);

  const { parcelas, totalComCondicao } = useParcelasCalc({
    total: totalProdutos,
    condicao: condicaoSelecionada
  });

  const totalItensCarrinho = useMemo(
    () => itens.reduce((sum, item) => sum + item.quantidade, 0),
    [itens]
  );

  // AIDEV-NOTE: Auto-selecao de tabela de preco por quantidade total de unidades
  // Regras ordenadas por 'ordem' — aplica a primeira que cobre a quantidade atual
  useEffect(() => {
    const regras = user?.regras_tabela_quantidade;
    if (!regras || regras.length === 0) return;

    const totalQtd = itens.reduce((sum, item) => sum + item.quantidade, 0);

    const regrasOrdenadas = [...regras].sort((a, b) => a.ordem - b.ordem);
    const regra = regrasOrdenadas.find(
      (r) =>
        totalQtd >= r.quantidade_minima &&
        (r.quantidade_maxima === null || totalQtd <= r.quantidade_maxima)
    );

    if (regra && regra.tabela_preco_id !== tabelaPrecoId) {
      setTabelaPreco(regra.tabela_preco_id);
      setTabToast(`Tabela de preço atualizada (${totalQtd} unidades)`);
    }
  }, [itens, user?.regras_tabela_quantidade, tabelaPrecoId, setTabelaPreco]);

  // AIDEV-NOTE: Mutation para criar rascunho — agora disparado na transicao step 0 → 1
  // (logo apos selecionar o cliente) para garantir persistencia desde o inicio
  const criarPedidoMutation = useMutation({
    mutationFn: async () => {
      if (!clienteSelecionado) throw new Error('Cliente nao selecionado');
      const currentTabelaPrecoId = usePedidoStore.getState().tabelaPrecoId;
      try {
        // AIDEV-NOTE: usa o service com fallback offline (Dexie + op_log) em vez de api.post direto —
        // garante que o rascunho seja criado mesmo sem conectividade (Task 6)
        const result = await pedidoClientService.criarPedido(
          {
            cliente_codigo: clienteSelecionado.CODIGO,
            cliente_nome: clienteSelecionado.RAZAO_SOCIAL,
            cliente_cpf_cnpj: clienteSelecionado.CNPJ_CPF ?? undefined,
            tabela_preco_id: currentTabelaPrecoId,
            itens: []
          },
          user?.vendedor_id ?? 0
        );
        return { pedido: { id: result.id, status: 'rascunho' }, offline: result.offline };
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { code?: string; error?: string } } };
        const code = axiosErr.response?.data?.code;
        if (code === 'VENDOR_NOT_LINKED') {
          throw new Error(
            'Usuário sem vendedor vinculado. Acesse Configurações → Usuários e vincule um vendedor a este usuário antes de criar pedidos.'
          );
        }
        const serverMsg = axiosErr.response?.data?.error;
        if (serverMsg) throw new Error(serverMsg);
        throw err;
      }
    },
    onSuccess: (data) => {
      setPedidoId(data.pedido.id);
      // AIDEV-NOTE: Finding 1 + 3 (final review) — marca o draft recem-criado como 'novo' (fluxo de
      // criacao, nunca de edicao) e registra o vendedor dono. Ver podeRecuperarDraft() em
      // pedido.store.ts — sem isso o dialogo de recuperacao da NovoPedidoPage nao consegue
      // distinguir este draft de um draft de edicao vazado ou de outro vendedor.
      setDraftOrigem('novo');
      setDraftVendedorId(user?.vendedor_id ?? null);
      if (data.offline) {
        setTabToast('Sem conexão — pedido criado localmente e será sincronizado');
      }
      // AIDEV-NOTE: Avanca para step 1 (catalogo) apos criar rascunho com sucesso
      setStepAtual(1);
    }
  });

  // AIDEV-NOTE: Fila serializada com retry — substitui o useMutation fire-and-forget.
  // Garante no maximo 1 PATCH /pedidos/:id/itens em voo e que o payload mais recente
  // sempre prevaleca, com retry automatico em backoff exponencial em caso de erro.
  // AIDEV-NOTE: criar E descartar dentro do MESMO effect (nao no corpo do render) —
  // com React.StrictMode (main.tsx) o effect roda mount→cleanup→mount em dev; se a
  // instancia fosse criada no corpo do render com "if (!ref.current)", o cleanup do
  // primeiro mount chamaria dispose() e o segundo mount NAO recriaria a fila (o if ja
  // seria false), deixando flush() sempre retornando cedo por disposed=true e o
  // autosave morto silenciosamente. Deps vazias: api e setAutoSaveStatus sao estaveis.
  const autosaveQueueRef = useRef<AutosaveQueue | null>(null);
  useEffect(() => {
    autosaveQueueRef.current = new AutosaveQueue(
      async ({ pedidoId: id, itens: itensPayload }) => {
        try {
          const res = await api.patch<{ revision?: number }>(`/pedidos/${id}/itens`, {
            itens: itensPayload,
            revision: revisionRef.current
          });
          revisionRef.current = res.data.revision ?? revisionRef.current;
        } catch (err) {
          // AIDEV-NOTE: Snapshot local no Dexie antes de classificar o erro — o rascunho deve
          // aparecer em /pedidos mesmo offline ou quando o erro for definitivo (413); o dado
          // local nao pode se perder em nenhum dos casos.
          await db.pedidos.update(id, {
            itens: itensPayload.map((i) => ({
              produto_id: i.produto_codigo,
              descricao: i.descricao,
              quantidade: i.quantidade,
              preco_unitario: i.valor_unitario,
              desconto: i.desconto_percentual
            })),
            valor_total: itensPayload.reduce(
              (s, i) =>
                s + i.quantidade * i.valor_unitario * (1 - (i.desconto_percentual ?? 0) / 100),
              0
            ),
            updated_at: Date.now()
          });

          // AIDEV-NOTE: mantem o payload da op CREATE_PEDIDO em dia com o carrinho. O rascunho
          // criado offline entra no op_log com itens: [] e, sem isto, o drain criava um pedido
          // VAZIO no servidor quando a conexao voltava — os itens digitados morriam no aparelho.
          // No-op quando o rascunho foi criado online (nao existe op pendente para este pedido).
          await opLogService
            .mesclarPayloadPendente('pedidos', id, 'CREATE_PEDIDO', { itens: itensPayload })
            .catch(() => undefined);

          const axiosErr = err as {
            response?: { status?: number; data?: { revision_atual?: number } };
          };
          // AIDEV-NOTE: 409 REVISION_CONFLICT — outra sessao alterou o rascunho antes deste PATCH.
          // Realinha revisionRef com o revision_atual devolvido pelo server (sem GET extra) antes
          // de relancar, para que o retry da fila reenvie com a revision correta.
          if (
            axiosErr.response?.status === 409 &&
            typeof axiosErr.response.data?.revision_atual === 'number'
          ) {
            revisionRef.current = axiosErr.response.data.revision_atual;
          }
          // AIDEV-NOTE: Finding 2 (final review) — 400 (INVALID_STATUS_FOR_AUTOSAVE, ex: draft de
          // edicao vazado apontando pra pedido ja finalizado/erro_sync), 403 (RBAC — vendedor sem
          // acesso ao cliente/pedido) e 413 (payload excede o limite de 5mb do server) sao erros
          // 4xx DETERMINISTICOS: nunca vao se resolver num retry, so queimam rede/bateria em loop
          // infinito e deixam Finalizar bloqueado para sempre (autoSaveStatus preso em 'error').
          // marcarNaoRetryavel descarta o pending e sinaliza erro definitivo pra AutosaveQueue.
          // 404 fica DE FORA de proposito — e um estado transitorio legitimo (rascunho criado
          // offline, ainda nao drenado do op_log pro server) e deve continuar retryavel.
          if (
            axiosErr.response?.status === 400 ||
            axiosErr.response?.status === 403 ||
            axiosErr.response?.status === 413
          ) {
            marcarNaoRetryavel(err);
          }
          throw err;
        }
      },
      (s) => setAutoSaveStatus(s)
    );
    return () => {
      autosaveQueueRef.current?.dispose();
      autosaveQueueRef.current = null;
    };
  }, []);

  // AIDEV-NOTE: Autosave ligado tambem em editMode quando o pedido ainda e rascunho —
  // o PATCH /itens so aceita status 'rascunho' no server, entao pedidos finalizado/erro_sync
  // em edicao continuam persistindo apenas no Finalizar.
  const autosaveAtivo = !!pedidoId && (!editMode || pedidoStatus === 'rascunho');

  // AIDEV-NOTE: Auto-save com debounce de 1500ms — dispara sempre que itens mudam
  // e um rascunho ja foi criado (pedidoId existe), incluindo edicao de pedido em rascunho.
  useEffect(() => {
    if (!autosaveAtivo || !pedidoId) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      autosaveQueueRef.current?.push({ pedidoId, itens });
    }, 1500);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [itens, pedidoId, autosaveAtivo]);

  // AIDEV-NOTE: Mutation para finalizar pedido — fica no wizard para que o botao
  // possa ser renderizado no footer fixo ao lado do botao "Voltar" (step 4).
  const finalizarMutation = useMutation({
    mutationFn: async (opts?: { confirmarReducao?: boolean }) => {
      if (!pedidoId) throw new Error('Pedido nao foi criado. Tente novamente desde o inicio.');
      if (!condicaoId) throw new Error('Condicao de pagamento nao selecionada');

      // AIDEV-NOTE: || null em vez de ?? undefined para garantir que 0 (invalido para .positive()) vire null
      const body: FinalizarPedidoDto = {
        condicao_pagto_id: Number(condicaoId),
        desconto_geral_percentual: descontoGeral > 0 ? descontoGeral : undefined,
        observacao: observacao || undefined,
        origem: origem || undefined,
        tipo_comercializacao_id: user?.tipo_comercializacao_padrao_id || null,
        tipo_movimento_receber_id: tipoMovimentoReceberId || null,
        itens: itens.map((item) => ({
          produto_codigo: Number(item.produto_codigo),
          referencia: item.referencia || String(item.produto_codigo),
          descricao: item.descricao,
          cor: item.cor || null,
          tamanho: item.tamanho || null,
          quantidade: Number(item.quantidade),
          valor_unitario: Number(item.valor_unitario),
          desconto_percentual: Number(item.desconto_percentual ?? 0),
          origem_estoque: item.origem_estoque || null,
          custo: item.custo != null ? Number(item.custo) : null,
          codbarras: item.codbarras || null,
          codcor: item.codcor != null ? Number(item.codcor) : null,
          codtamanho: item.codtamanho != null ? Number(item.codtamanho) : null
        })),
        parcelas,
        // AIDEV-NOTE: Guarda de regressao (Task 8) — so vai true quando o usuario confirma
        // explicitamente no dialog apos o server recusar com 422 REDUCAO_SUSPEITA.
        confirmar_reducao: opts?.confirmarReducao
      };

      // AIDEV-TODO: remover console.log apos diagnostico do erro 400
      console.log('[finalizar] body enviado:', JSON.stringify(body, null, 2));
      try {
        // AIDEV-NOTE: passa pelo pedidoClientService (nao api.post direto) para herdar o fallback
        // Dexie + op_log. Sem isso o Finalizar era o UNICO passo do fluxo sem caminho offline: com
        // sinal fraco o vendedor ficava preso na tela de confirmacao e perdia o pedido digitado.
        const { pedido, offline } = await pedidoClientService.finalizarPedido(
          pedidoId,
          body,
          user?.vendedor_id ?? 0,
          {
            cliente_codigo: clienteSelecionado?.CODIGO ?? 0,
            cliente_nome: clienteSelecionado?.RAZAO_SOCIAL ?? '',
            cliente_cpf_cnpj: clienteSelecionado?.CNPJ_CPF ?? null,
            tabela_preco_id: tabelaPrecoId,
            total_produtos: totalProdutos,
            total_final: totalComCondicao
          }
        );
        return { pedido, offline };
      } catch (err: unknown) {
        // AIDEV-NOTE: Extrai detalhes de validacao Zod do response 400 para mensagem util ao usuario
        const axiosErr = err as {
          response?: {
            data?: {
              error?: string;
              code?: string;
              details?: Array<{ message?: string; path?: unknown[] }>;
            };
          };
        };
        // AIDEV-NOTE: Guarda de regressao (Task 8) — 422 REDUCAO_SUSPEITA nao e um erro generico,
        // abre o dialog de confirmacao em vez de lancar (evita cair no Alert de erro do wizard)
        if (axiosErr.response?.data?.code === 'REDUCAO_SUSPEITA') {
          setConfirmReducao(axiosErr.response.data.error ?? 'Reducao suspeita no total do pedido.');
          return undefined;
        }
        const details = axiosErr.response?.data?.details;
        if (details && details.length > 0) {
          const msgs = details.map((d) => d.message ?? String(d)).join('; ');
          throw new Error(`Erro de validacao: ${msgs}`);
        }
        const serverMsg = axiosErr.response?.data?.error;
        if (serverMsg) throw new Error(serverMsg);
        throw err;
      }
    },
    onSuccess: (resultado) => {
      // AIDEV-NOTE: resultado undefined significa que o REDUCAO_SUSPEITA abriu o dialog acima —
      // nao ha pedido finalizado ainda, entao nao dispara o fluxo de sucesso/compartilhamento.
      if (!resultado) return;
      setPedidoFinalizado(resultado.pedido);
      setFinalizadoOffline(resultado.offline);
      setShareOpen(true);
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      queryClient.invalidateQueries({ queryKey: ['pedido', pedidoId] });
    }
  });

  const handleShareClose = () => {
    setShareOpen(false);
    const id = pedidoId;
    resetPedido();
    navigate(`/pedidos/${id}`);
  };

  // AIDEV-NOTE: Validacao de se pode avancar em cada step
  const canGoForward = useCallback(() => {
    switch (stepAtual) {
      case 0:
        return !!clienteSelecionado;
      case 1:
        return itens.length > 0;
      case 2:
        // AIDEV-NOTE: condicao de pagamento foi movida para Step4 — Step3 so precisa de itens
        return itens.length > 0;
      case 3:
        return !!condicaoId;
      default:
        return false;
    }
  }, [stepAtual, clienteSelecionado, itens.length, condicaoId]);

  const handleVoltar = useCallback(() => {
    if (stepAtual > 0) {
      setStepAtual(stepAtual - 1);
    }
  }, [stepAtual, setStepAtual]);

  // AIDEV-NOTE: Ao avancar do step 0, cria o rascunho se ainda nao existe (modo criacao).
  // Em editMode o pedidoId ja existe — pula direto para avancar o step.
  const handleAvancar = useCallback(() => {
    if (!canGoForward()) return;
    if (stepAtual === 0 && !pedidoId && !editMode) {
      criarPedidoMutation.mutate();
      return;
    }
    if (stepAtual < STEPS.length - 1) {
      setStepAtual(stepAtual + 1);
    }
  }, [stepAtual, setStepAtual, canGoForward, pedidoId, editMode, criarPedidoMutation]);

  const renderStep = () => {
    switch (stepAtual) {
      case 0:
        return <Step1ClienteSelect />;
      case 1:
        return <Step2Catalogo />;
      case 2:
        return (
          <Step3CarrinhoManaged
            descontoGeral={descontoGeral}
            onDescontoGeralChange={setDescontoGeral}
          />
        );
      case 3:
        return (
          <Step4PagamentoManaged
            descontoGeral={descontoGeral}
            condicaoId={condicaoId}
            onCondicaoChange={setCondicaoId}
            condicoes={condicoes}
            parcelas={parcelas}
            totalComCondicao={totalComCondicao}
            totalProdutos={totalProdutos}
            observacao={observacao}
            onObservacaoChange={setObservacao}
            origem={origem}
            onOrigemChange={setOrigem}
            origens={origens}
            tipoMovimentoReceberId={tipoMovimentoReceberId}
            onTipoMovimentoReceberChange={setTipoMovimentoReceberId}
            tiposMovimentoReceber={tiposMovimentoReceber}
            restricoes={user?.restricoes_condicao_pagto_jwt ?? []}
            podeAlterarOrigem={user?.pode_alterar_origem_pedido ?? true}
            podeAlterarTipoMovimento={user?.pode_alterar_tipo_movimento_receber ?? true}
          />
        );
      case 4:
        return (
          <Step5Confirmacao
            descontoGeral={descontoGeral}
            observacao={observacao}
            origem={origem}
            condicaoPagtoId={condicaoId}
            condicaoSelecionada={condicaoSelecionada}
            parcelas={parcelas}
            totalProdutos={totalProdutos}
            totalComCondicao={totalComCondicao}
            tipoMovimentoReceberId={tipoMovimentoReceberId}
            tipoComercializacaoId={user?.tipo_comercializacao_padrao_id ?? null}
            finalizarIsError={finalizarMutation.isError}
            finalizarError={
              finalizarMutation.error instanceof Error ? finalizarMutation.error : null
            }
          />
        );
      default:
        return null;
    }
  };

  // AIDEV-NOTE: Indicador visivel em TODOS os steps com rascunho criado — no incidente
  // de 2026-08 o vendedor finalizou sem ver que o autosave estava em erro (ficava oculto no step 4).
  const renderAutoSaveIndicator = () => {
    if (!pedidoId || autoSaveStatus === 'idle') return null;

    const configs = {
      saving: {
        icon: <CloudSyncIcon fontSize="inherit" />,
        label: 'Salvando...',
        color: 'text.secondary'
      },
      saved: { icon: <CloudDoneIcon fontSize="inherit" />, label: 'Salvo', color: 'success.main' },
      error: {
        icon: <CloudOffIcon fontSize="inherit" />,
        label: 'Erro ao salvar',
        color: 'warning.main'
      }
    } as const;

    const cfg = configs[autoSaveStatus];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="caption"
          color={cfg.color}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          {cfg.icon} {cfg.label}
        </Typography>
      </Box>
    );
  };

  // AIDEV-NOTE: flex: 1 + minHeight: 0 garante que o wizard preencha o espaco disponivel
  // no NovoPedidoPage sem transbordar. overflow: hidden impede que conteudo interno
  // escape e cause scroll no MainLayout, mantendo o footer sempre visivel no mobile.
  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}
    >
      {/* AIDEV-NOTE: px reduzido no mobile + labels ocultas para economizar altura vertical */}
      <Stepper
        activeStep={stepAtual}
        sx={{
          px: { xs: 1, sm: 3 },
          pt: 2,
          pb: 1,
          '& .MuiStepLabel-label': { display: { xs: 'none', sm: 'block' } }
        }}
      >
        {STEPS.map((label, idx) => (
          <MuiStep key={label}>
            <StepLabel>
              {idx === 2 && totalItensCarrinho > 0 ? (
                <Badge badgeContent={totalItensCarrinho} color="primary" max={99}>
                  <Box component="span" sx={{ pr: 1.5 }}>
                    {label}
                  </Box>
                </Badge>
              ) : (
                label
              )}
            </StepLabel>
          </MuiStep>
        ))}
      </Stepper>

      {/* AIDEV-NOTE: display:flex+column aqui permite que steps usem flex:1 para preencher altura */}
      <Box
        sx={{ flex: 1, overflow: 'hidden', minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        {renderStep()}
      </Box>

      {/* AIDEV-NOTE: Rodape fixo sempre visivel no mobile. flexShrink:0 impede que o flex
          layout comprima o footer. pb usa safe-area-inset-bottom para notch de iPhone. */}
      {stepAtual < 4 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            pt: { xs: 1.5, sm: 2 },
            pb: { xs: 'max(env(safe-area-inset-bottom, 0px), 12px)', sm: 2 },
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          {/* AIDEV-NOTE: Em editMode step 0 mostra Cancelar em vez de Voltar desabilitado */}
          {editMode && stepAtual === 0 && onCancel ? (
            <Button variant="outlined" color="inherit" size="large" onClick={onCancel}>
              Cancelar
            </Button>
          ) : (
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBackIcon />}
              onClick={handleVoltar}
              disabled={stepAtual === 0}
              sx={{ minWidth: { xs: 110, sm: 'auto' } }}
            >
              Voltar
            </Button>
          )}

          {/* Indicador de auto-save + botao Avancar */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            {renderAutoSaveIndicator()}
            {/* Erro ao criar rascunho (step 0) */}
            {stepAtual === 0 && criarPedidoMutation.isError && (
              <Alert severity="error" sx={{ py: 0 }}>
                {criarPedidoMutation.error instanceof Error
                  ? criarPedidoMutation.error.message
                  : 'Erro ao criar pedido. Tente novamente.'}
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={handleAvancar}
              disabled={!canGoForward() || criarPedidoMutation.isPending}
              sx={{ minWidth: { xs: 110, sm: 'auto' } }}
            >
              {stepAtual === 0 && criarPedidoMutation.isPending ? 'Criando...' : 'Avancar'}
            </Button>
          </Box>
        </Box>
      )}

      {/* AIDEV-NOTE: Alerta de autosave em erro no step 4 — retry manual antes de finalizar
          (ver renderAutoSaveIndicator acima sobre o incidente de 2026-08) */}
      {stepAtual === 4 && autosaveAtivo && autoSaveStatus === 'error' && (
        <Alert
          severity="info"
          sx={{ mx: 2, mb: 1 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => pedidoId && autosaveQueueRef.current?.push({ pedidoId, itens })}
            >
              Tentar novamente
            </Button>
          }
        >
          Sem conexão com o servidor. Você pode finalizar assim mesmo — o pedido fica salvo no
          aparelho e sobe sozinho quando a rede voltar.
        </Alert>
      )}

      {/* AIDEV-NOTE: Footer do step 4 — Voltar + indicador de auto-save + Finalizar na mesma linha (footer fixo) */}
      {stepAtual === 4 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            px: { xs: 2, sm: 3 },
            pt: { xs: 1.5, sm: 2 },
            pb: { xs: 'max(env(safe-area-inset-bottom, 0px), 12px)', sm: 2 },
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Button
            variant="outlined"
            size="large"
            startIcon={<ArrowBackIcon />}
            onClick={handleVoltar}
            disabled={finalizarMutation.isPending}
          >
            Voltar
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {renderAutoSaveIndicator()}
            <Button
              variant="contained"
              size="large"
              color="success"
              startIcon={
                finalizarMutation.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
              onClick={() => finalizarMutation.mutate(undefined)}
              disabled={
                finalizarMutation.isPending ||
                !condicaoId ||
                // AIDEV-NOTE: APENAS 'saving' bloqueia (espera curta do debounce, ~1,5s).
                // 'error' NAO bloqueia mais: offline a AutosaveQueue fica em retry infinito e o
                // status nunca sai de 'error', o que deixava o Finalizar morto para sempre e fazia
                // o vendedor perder o pedido digitado (relato de campo 2026-10). O caminho offline
                // do finalizar grava no op_log, entao fechar sem o PATCH ter subido e seguro.
                (autosaveAtivo && autoSaveStatus === 'saving')
              }
              sx={{ minWidth: { xs: 160, sm: 'auto' } }}
            >
              {finalizarMutation.isPending ? 'Finalizando...' : 'Finalizar Pedido'}
            </Button>
          </Box>
        </Box>
      )}

      {/* AIDEV-NOTE: Toast de auto-selecao de tabela de preco por regras de quantidade */}
      <Snackbar
        open={!!tabToast}
        autoHideDuration={3000}
        onClose={() => setTabToast(null)}
        message={tabToast}
      />

      {/* AIDEV-NOTE: Aviso persistente (sem autoHide) quando o pedido fechou pelo caminho offline.
          Fica no topo para continuar visivel por cima do ShareModal — o PDF e gerado client-side,
          entao o vendedor ainda consegue mandar o pedido pro cliente na hora, sem rede. */}
      <Snackbar
        open={!!pedidoFinalizado && finalizadoOffline}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" icon={<CloudOffIcon fontSize="inherit" />}>
          Pedido salvo no aparelho. Será enviado automaticamente quando a conexão voltar.
        </Alert>
      </Snackbar>

      {/* ShareModal exibido apos finalizacao bem-sucedida */}
      {pedidoFinalizado && (
        <ShareModal open={shareOpen} onClose={handleShareClose} pedido={pedidoFinalizado} />
      )}

      {/* AIDEV-NOTE: Guarda de regressao (Task 8) — dialog exibido quando o server recusa o
          finalizar com 422 REDUCAO_SUSPEITA (total caiu >50% vs rascunho salvo). "Sim" reenvia
          com confirmar_reducao: true; "Revisar itens" apenas fecha o dialog sem reenviar. */}
      <Dialog open={!!confirmReducao} onClose={() => setConfirmReducao(null)}>
        <DialogTitle>Confirma a redução do pedido?</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmReducao}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReducao(null)}>Revisar itens</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => {
              setConfirmReducao(null);
              finalizarMutation.mutate({ confirmarReducao: true });
            }}
          >
            Sim, finalizar assim mesmo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
